/**
 * /el/guides/excel-organosi-gamou
 *
 * The Greek counterpart of `wedding-planning-spreadsheet-what-to-track.en.js`.
 *
 * ── The intent gap, and how it is handled ────────────────────────────────
 * The Greek query behind this subject is not the English one. English readers
 * search for what to track; Greek readers overwhelmingly search for a
 * downloadable template — «excel για γάμο», «πρότυπο», «δωρεάν αρχείο». That is
 * a different promise, and one this article cannot keep: Agapaé does not
 * publish a spreadsheet, and inventing one to satisfy the query would be a
 * marketing decision dressed as a guide.
 *
 * So the article says so, in its own second section, and then makes the honest
 * trade explicit: templates are trivially easy to find and the columns are the
 * hard part, so what follows is the columns — the thing that makes ANY template
 * yours. That is a real answer to the real query rather than a bait, and it is
 * the reason this translation is not a bounce.
 *
 * The closing section keeps the English module's denial and gives it more
 * weight than the English original does, because here it answers a live
 * expectation: Agapaé does not import an existing spreadsheet and does not
 * synchronise with Excel or Google Sheets. Never soften that sentence.
 *
 * ── Greek rather than translated ─────────────────────────────────────────
 *  - **Excel by name**, not «λογιστικό φύλλο». It is what the market says, and
 *    §6 of the brief keeps English market terms where Greek searchers use them.
 *  - **Ο Νικολάου (4)** — the household-row example, which is exactly how a
 *    Greek list is written and exactly why the person has to be the unit.
 *  - **The «side» column** is «από ποια πλευρά», which is how a Greek couple
 *    actually annotates a guest list.
 *
 * ── The hedges, which are load-bearing ───────────────────────────────────
 * There is no guest count at which a spreadsheet stops working, and no correct
 * update interval. Both are stated. A couple whose single file works perfectly
 * is not doing it wrong, and the article never argues them out of it — the
 * information/decision/action split is offered as a way of thinking and
 * explicitly labelled as not a methodology.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * All in the closing section and all already published on /el/wedding-planning:
 * the editable categories, a vendor card with its status, a task with due date,
 * priority, checklist and labels and an overdue flag, a cost with its label,
 * amount, due date and vendor rolling into four totals, decisions with their
 * options and answer, documents filed against a vendor or task, the shared
 * calendar and the timeline, and Éros reading the space and making a change
 * when asked.
 *
 * And the denials, all real: no spreadsheet import, no Excel or Google Sheets
 * synchronisation, not a vendor directory, nothing contacted, booked or paid
 * through it, no reminders about vendor payments, not accounting software, no
 * substitute for signed agreements.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const weddingPlanningSpreadsheetWhatToTrackEl = {
  slug: "excel-organosi-gamou",

  title: "Excel για την οργάνωση του γάμου: τι αξίζει να καταγράφετε",
  metaTitle: "Excel για την οργάνωση του γάμου: τι να καταγράφετε | Agapaé",
  metaDescription:
    "Τι πρέπει να κρατά ένα αρχείο οργάνωσης γάμου: εργασίες, καλεσμένοι, συνεργάτες, προϋπολογισμός, πληρωμές, χρονοδιάγραμμα, αποφάσεις και έγγραφα.",

  eyebrow: "Οργάνωση γάμου",

  hero: {
    src: "/assets/photos/venice_luxury_wedding_photographer_1_von_1.jpg",
    alt: "Ασπρόμαυρη φωτογραφία με μακριά ξύλινα τραπέζια στρωμένα για δείπνο κάτω από δέντρα το σούρουπο, με κεριά αναμμένα σε όλο τους το μήκος ανάμεσα σε ποτήρια και χαμηλές ανθοσυνθέσεις, και τις καρέκλες ακόμη άδειες",
    width: 2400,
    height: 1602,
  },

  intro: [
    `Οι περισσότεροι γάμοι ξεκινούν από ένα αρχείο Excel. Ένα φύλλο, λίγες στήλες, μια λίστα με πράγματα που πρέπει να γίνουν — και για τις πρώτες εβδομάδες είναι το πιο καθησυχαστικό έγγραφο που έχετε.`,
    `Αυτό που ακολουθεί είναι αρκετά προβλέψιμο. Τα φύλλα πολλαπλασιάζονται. Προστίθενται στήλες που δεν συμπληρώνονται ποτέ. Ο ίδιος αριθμός εμφανίζεται σε δύο σημεία και διαφωνεί με τον εαυτό του. Κάπου στον τέταρτο μήνα, το αρχείο παύει να είναι εκεί όπου κοιτάτε και γίνεται άλλο ένα πράγμα που πρέπει να κρατήσετε ζωντανό.`,
    `Αυτός ο οδηγός είναι για το τι αξίζει πραγματικά μια θέση μέσα του: την πληροφορία που θα χρειαστεί να ξαναβρείτε, τις αποφάσεις που έχετε ήδη πάρει, και τη δουλειά που περιμένει ακόμη κάποιον.`,
  ],

  sections: [
    {
      heading: "Τι πρέπει να κρατά το αρχείο σας",
      content: [
        `Ένα αρχείο οργάνωσης γάμου πρέπει να κρατά ό,τι αλλιώς θα χρειαζόταν να ξαναφτιάξετε από την αρχή: τι μένει να γίνει, ποιοι έρχονται, ποιους έχετε κλείσει, τι κοστίζει, τι έχει πληρωθεί, τι έχετε αποφασίσει και πότε πέφτει το καθένα.`,
        `Στην πράξη αυτό είναι οκτώ περιοχές — εργασίες, καλεσμένοι, συνεργάτες, προϋπολογισμός, πληρωμές, χρονοδιάγραμμα, αποφάσεις και έγγραφα — και σχεδόν κάθε έτοιμο πρότυπο καλύπτει τις πέντε πρώτες. Οι τρεις τελευταίες είναι εκεί όπου τα περισσότερα αρχεία υποχωρούν αθόρυβα, γιατί κρατούν ένα πλάνο μαζί αντί να το απαριθμούν.`,
        `Ζητούμενο δεν είναι η πληρότητα. Ένα αρχείο που παρακολουθεί τα πάντα είναι ένα αρχείο που δεν ενημερώνει κανείς, και ένα ξεπερασμένο είναι χειρότερο από ένα σύντομο: μοιάζει έγκυρο και είναι λάθος.`,
      ],
    },
    {
      heading: "Αν ήρθατε για ένα έτοιμο πρότυπο",
      content: [
        `Οι περισσότεροι φτάνουν εδώ ψάχνοντας ένα έτοιμο αρχείο να κατεβάσουν. Ας το ξεκαθαρίσουμε πριν προχωρήσουμε.`,
        `Δεν δίνουμε πρότυπο Excel. Και δεν είναι εκεί το πρόβλημα ούτως ή άλλως: έτοιμα αρχεία βρίσκετε δεκάδες, δωρεάν, σε πέντε λεπτά.`,
        `Το δύσκολο δεν είναι το αρχείο. Είναι οι στήλες — το να ξέρετε τι πρέπει να κρατά η κάθε γραμμή ώστε να μπορείτε να απαντήσετε σε μια ερώτηση χωρίς να ανοίξετε τίποτε άλλο. Ένα πρότυπο που δεν καταλαβαίνετε γιατί έχει τις στήλες που έχει, θα το εγκαταλείψετε τον τρίτο μήνα.`,
        `Αυτό που ακολουθεί είναι λοιπόν οι στήλες. Πάρτε όποιο πρότυπο θέλετε και κάντε το δικό σας, ή ξεκινήστε από ένα λευκό φύλλο — δουλεύουν και τα δύο.`,
      ],
    },
    {
      heading: "Πληροφορία, αποφάσεις και ενέργειες",
      content: [
        `Ένα αρχείο οργάνωσης κρατά τρία διαφορετικά πράγματα ταυτόχρονα, και το να τα ξεχωρίσετε είναι ο πιο γρήγορος τρόπος να καταλάβετε σε τι χρησιμεύει η κάθε στήλη.`,
        { type: "subheading", text: "Πληροφορία" },
        `Πράγματα που πρέπει να θυμάστε: ένα τηλέφωνο, τη διεύθυνση του χώρου, τι καλύπτει μια συμφωνία, τι δεν τρώει ένας καλεσμένος. Αλλάζει σπάνια, και η μόνη της δουλειά είναι να βρίσκεται.`,
        { type: "subheading", text: "Αποφάσεις" },
        `Πράγματα που έχετε επιλέξει: τον φωτογράφο που κλείσατε, το μενού που καταλήξατε, τη διάταξη που συμφωνήσατε με τον χώρο. Μια απόφαση είναι τελειωμένη και όχι εκκρεμής, αλλά θέλει καταγραφή, γιατί σε έξι εβδομάδες κάποιος θα ρωτήσει τι αποφασίσατε.`,
        { type: "subheading", text: "Ενέργειες" },
        `Πράγματα που πρέπει ακόμη να κάνει κάποιος: να στείλει τους τελικούς αριθμούς, να διαβάσει τη σύμβαση, να επιβεβαιώσει την ώρα παράδοσης. Μια ενέργεια έχει υπεύθυνο και ημερομηνία, και είναι το μόνο από τα τρία που παλιώνει μόνο του.`,
        `Τα περισσότερα αρχεία κρατούν καλά την πληροφορία, άσχημα τις ενέργειες και καθόλου τις αποφάσεις — έτσι ένα πλάνο μοιάζει οργανωμένο και σας αφήνει να μην ξέρετε τι έχει κλείσει.`,
        `Δίνει επίσης μια χρήσιμη δοκιμή για κάθε νέα στήλη: ποιο από τα τρία κρατά, και μήπως αυτό το είδος ζει ήδη κάπου αλλού;`,
        {
          type: "note",
          text: `Είναι ένας πρακτικός τρόπος να ταξινομήσετε την πληροφορία της οργάνωσης, όχι κάποια καθιερωμένη μεθοδολογία. Χρησιμοποιήστε τον αν σας διευκολύνει, και αγνοήστε τον αν όχι.`,
        },
      ],
    },
    {
      heading: "Μία πηγή αλήθειας",
      content: [
        `Η πιο συχνή αποτυχία δεν είναι η ακαταστασία· είναι η διπλοεγγραφή: η λίστα καλεσμένων στο αρχείο και στις σημειώσεις του κινητού, μια προσφορά σε ένα email και ξαναγραμμένη στον προϋπολογισμό, δύο αντίγραφα του ίδιου αρχείου με ελαφρώς διαφορετικό όνομα.`,
        `Ό,τι ακολουθεί είναι προβλέψιμο:`,
        {
          type: "list",
          items: [
            `ο ίδιος αριθμός εμφανίζεται δύο φορές, και οι δύο διαφωνούν`,
            `το ένα αντίγραφο ενημερώνεται και το άλλο όχι`,
            `κανείς δεν είναι σίγουρος ποια εκδοχή ισχύει`,
            `μια αλλαγή πέφτει ανάμεσά τους και δεν φτάνει σε καμία`,
          ],
        },
        `Η λύση δεν είναι περισσότερη πειθαρχία. Είναι να δώσετε σε κάθε είδος πληροφορίας ακριβώς ένα σπίτι και να δείχνουν όλα τα υπόλοιπα προς αυτό, ώστε όταν αλλάζει κάτι να αλλάζει σε ένα σημείο.`,
        `Μια στήλη ανήκει εκεί όπου ζει ήδη αυτό που περιγράφει: η επιλογή μενού ενός καλεσμένου κάθεται στη λίστα καλεσμένων, και το φαγητό διαβάζει το σύνολό του από εκεί.`,
      ],
    },
    {
      heading: "Εργασίες, όχι κατηγορίες",
      content: [
        { type: "subheading", text: "Τι κάνει μια εργασία εργασία" },
        `Τα περισσότερα checklist γάμου είναι λίστες κατηγοριών: λουλούδια, μουσική, μεταφορά, προσκλητήρια. Είναι καλά για να μην ξεχαστεί τίποτα και εντελώς άχρηστα για να γίνει κάτι.`,
        `Το «λουλούδια» δεν είναι εργασία: κανείς δεν μπορεί να το ξεκινήσει, να το τελειώσει ή να σας πει αν έχει αργήσει. Το «διαλέξτε ανθοπωλείο και κλείστε το πακέτο» είναι εργασία, γιατί έχει μια στιγμή στην οποία είναι αναμφίβολα τελειωμένη.`,
        `Αυτή είναι η δοκιμή: αν δύο άνθρωποι διαβάσουν μια γραμμή και διαφωνήσουν για το αν έχει τελειώσει, είναι κατηγορία μεταμφιεσμένη σε ρήμα και θέλει σπάσιμο στα δύο τρία πράγματα που όντως πρέπει να γίνουν.`,
        { type: "subheading", text: "Ημερομηνία και υπεύθυνος σε κάθε εργασία" },
        `Μια εργασία χωρίς ημερομηνία και χωρίς όνομα δίπλα της δεν κινείται: μια λίστα χωρίς ημερομηνίες και χωρίς υπεύθυνους δεν έχει τρόπο να σας πει ποια γραμμή αξίζει τη σημερινή μέρα.`,
        `Μια γραμμή που δουλεύει κρατά την εργασία γραμμένη ως ενέργεια, έναν υπεύθυνο, μια προθεσμία, μια κατάσταση και μια σημείωση όπου ο τίτλος δεν φτάνει.`,
        `Το «έναν» υπεύθυνο μετράει περισσότερο απ' όσο ακούγεται. Η κοινή ευθύνη μοιάζει δίκαιη και στην πράξη σημαίνει ότι δεν την έχει κανείς: δύο από τους πιο συνηθισμένους λόγους που αργεί μια εργασία γάμου είναι ότι ο καθένας νόμιζε πως την είχε ο άλλος, και ότι κανείς δεν ήθελε να ρωτήσει.`,
        `Κρατήστε και τις καταστάσεις λίγες και δικές σας — τρεις λέξεις, πέντε ή ένα απλό κουτάκι δουλεύουν όλα, αρκεί η καθεμία να σημαίνει το ίδιο κάθε φορά.`,
        `Και μια προθεσμία είναι πιο χρήσιμη ως η μέρα που κάτι πρέπει να ξεκινήσει παρά ως η μέρα που πρέπει να έχει τελειώσει.`,
      ],
    },
    {
      heading: "Η λίστα καλεσμένων θέλει δικό της χώρο",
      content: [
        `Η λίστα αλλάζει περισσότερο και αγγίζει περισσότερα. Οι αριθμοί του φαγητού, τα τραπέζια, τα έντυπα και αρκετές ποσότητες συνεργατών διαβάζουν από αυτήν — γι' αυτό πρέπει να ζει σε ένα σημείο και τα υπόλοιπα να δείχνουν προς αυτό αντί να την αντιγράφουν.`,
        `Θέλει μία γραμμή ανά άτομο, που να κρατά:`,
        {
          type: "list",
          items: [
            `το όνομα, όπως πρέπει να γραφτεί στο προσκλητήριο`,
            `το σπίτι ή την πρόσκληση στην οποία ανήκει`,
            `από ποια πλευρά ή παρέα έρχεται`,
            `στοιχεία επικοινωνίας, σε όποια μορφή θα χρησιμοποιήσετε πραγματικά`,
            `αν στάλθηκε πρόσκληση, και την απάντησή του`,
            `τον συνοδό, όπου υπάρχει`,
            `ό,τι θα χρειαστεί ένας συνεργάτης: μια διατροφική ιδιαιτερότητα, μια ανάγκη πρόσβασης, την ηλικία ενός παιδιού`,
          ],
        },
        `Η μονάδα είναι το άτομο, γιατί μια γραμμή που λέει «οικογένεια Νικολάου (4)» δεν μπορεί να καθίσει σε τραπέζι, να μετρηθεί ή να δοθεί στο catering με σιγουριά. Η πρόσκληση είναι δικό της πεδίο, ώστε να καλείτε ανά σπίτι και να μετράτε ανά άτομο χωρίς να συντηρείτε δύο λίστες.`,
        {
          type: "p",
          text: [
            `Το χτίσιμο και η ομαδοποίηση της λίστας είναι μεγάλο θέμα από μόνο του, και το `,
            {
              text: "Λίστα καλεσμένων γάμου: πώς να την οργανώσετε",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` το καλύπτει ολόκληρο, μαζί με τις απαντήσεις.`,
          ],
        },
      ],
    },
    {
      heading: "Συνεργάτες και τι έχετε συμφωνήσει",
      content: [
        `Μια λίστα με ονόματα και τηλέφωνα απαντά στην εύκολη ερώτηση. Η δύσκολη, μήνες αργότερα, είναι τι ακριβώς συμφωνήσατε — και αυτό σπάνια είναι γραμμένο κάπου εκτός από ένα χαρτί που δεν άνοιξε ξανά κανείς.`,
        `Μια γραμμή συνεργάτη αξίζει να κρατά:`,
        {
          type: "list",
          items: [
            `την επιχείρηση και το άτομο με το οποίο μιλάτε`,
            `τα στοιχεία επικοινωνίας`,
            `τι παρέχουν`,
            `πού έχει φτάσει η συζήτηση: επικοινωνία, προσφορά, κλείσιμο, υπογραφή`,
            `αν υπάρχει συμφωνητικό, και πού βρίσκεται`,
            `το συμφωνημένο σύνολο`,
            `τις ημερομηνίες που μετράνε: άφιξη, στήσιμο, παράδοση, πόσο μένουν`,
            `τι περιμένετε από αυτούς`,
          ],
        },
        `Η κατάσταση είναι η στήλη που δικαιώνει τη θέση της. Δύο συνεργάτες με την ίδια τιμή βρίσκονται σε τελείως διαφορετική θέση, αν ο ένας έχει υπογράψει και ο άλλος δεν έχει απαντήσει σε email εδώ και τρεις εβδομάδες.`,
        {
          type: "p",
          text: [
            `Το να κρατιούνται μαζί ο συνεργάτης, η συμφωνία του και οι πληρωμές του είναι θέμα από μόνο του: το `,
            {
              text: "Συνεργάτες και πληρωμές γάμου: πώς να τα παρακολουθείτε",
              href: "/guides/how-to-track-wedding-vendors-payments",
            },
            ` καλύπτει ολόκληρη την καταγραφή.`,
          ],
        },
      ],
    },
    {
      heading: "Οι πληρωμές χωριστά από τον προϋπολογισμό",
      content: [
        `Τα δύο μπαίνουν συνεχώς στο ίδιο φύλλο, και αυτή η συγχώνευση είναι που κάνει τους αριθμούς αναξιόπιστους. Ο προϋπολογισμός είναι πλάνο· οι πληρωμές είναι γεγονότα.`,
        `Αξίζει να κρατάτε πέντε μεγέθη χωριστά:`,
        {
          type: "list",
          items: [
            `εκτιμώμενο, δηλαδή τι νομίζετε τώρα ότι μπορεί να κοστίσει κάτι`,
            `προσφορά, δηλαδή τι έχει πει ένας συνεργάτης ότι θα κοστίσει`,
            `δεσμευμένο, δηλαδή σε τι έχετε συμφωνήσει, πληρωμένο ή όχι`,
            `πληρωμένο, δηλαδή τι έχει όντως φύγει από τον λογαριασμό σας`,
            `υπόλοιπο, δηλαδή δεσμευμένο μείον πληρωμένο, που είναι χρήματα που ήδη οφείλετε`,
          ],
        },
        `Η παγίδα είναι να αφαιρέσετε τα πληρωμένα από τον προϋπολογισμό και να διαβάσετε το αποτέλεσμα ως αυτό που σας μένει. Δεν είναι: έτσι όλα όσα έχετε δεσμεύσει και δεν έχετε πληρώσει μοιάζουν ακόμη διαθέσιμα.`,
        `Σε όρους αρχείου, αυτό είναι δύο περιοχές και όχι μία. Μια γραμμή προϋπολογισμού ανά κατηγορία ή αντικείμενο, με το εκτιμώμενο, την προσφορά και το δεσμευμένο· και μια γραμμή ανά πληρωμή, με τον συνεργάτη, μια περιγραφή, το ποσό, την ημερομηνία και το αν έχει γίνει.`,
        {
          type: "p",
          text: [
            `Η πλευρά των χρημάτων έχει δικό της οδηγό: ο `,
            {
              text: "Προϋπολογισμός γάμου: πώς να τον οργανώσετε",
              href: "/guides/how-to-create-wedding-budget",
            },
            ` καλύπτει το πώς ορίζεται το σύνολο, πώς μοιράζεται και πώς διαβάζεται το τι μένει πραγματικά.`,
          ],
        },
      ],
    },
    {
      heading: "Αποφάσεις και ανοιχτά ερωτήματα",
      content: [
        `Αυτή είναι η περιοχή που δεν έχει σχεδόν κανένα πρότυπο. Ένας γάμος είναι μια μακριά σειρά επιλογών, και ένα πλάνο που καταγράφει μόνο εργασίες και κόστη χάνει και τα δύο μισά της: τι αποφασίστηκε και τι δεν έχει αποφασιστεί ακόμη.`,
        `Το καθένα φέρνει τον δικό του μπελά. Ένα ανοιχτό ερώτημα μπλοκάρει αθόρυβα δουλειά: δεν μπορείτε να μιλήσετε στο ανθοπωλείο πριν κλείσουν τα χρώματα, και κανείς δεν το προσέχει μέχρι να ρωτήσει το ανθοπωλείο. Ένα κλεισμένο ερώτημα που δεν καταγράφηκε ανοίγει ξανά τρεις μήνες μετά, όταν ο ένας θυμάται ότι συμφωνήσατε μπουφέ και ο άλλος ότι συμφωνήσατε να το σκεφτείτε.`,
        `Μια γραμμή απόφασης είναι σύντομη:`,
        {
          type: "list",
          items: [
            `το ερώτημα, γραμμένο ως ερώτημα`,
            `αν είναι ανοιχτό ή κλειστό`,
            `η απάντηση, μόλις υπάρξει`,
            `μέχρι πότε πρέπει να απαντηθεί, όπου κάτι το περιμένει`,
            `μια γραμμή για το γιατί, γιατί το σκεπτικό είναι αυτό που εξαφανίζεται πρώτο`,
          ],
        },
        `Το τελευταίο δικαιώνει τη στήλη του. Οι περισσότερες αποφάσεις ξανανοίγουν όχι επειδή η απάντηση ήταν λάθος, αλλά επειδή κανείς δεν θυμάται τι ζυγίστηκε απέναντί της — και μια γραμμή, «σερβιριστό αντί για μπουφέ, γιατί η αίθουσα δεν σηκώνει ουρά», συνήθως τελειώνει τη συζήτηση πριν ξαναρχίσει.`,
        `Διαβασμένη από πάνω προς τα κάτω, σας δίνει κάτι που καμία λίστα εργασιών δεν μπορεί: μια ειλικρινή εικόνα του πόσος γάμος έχει πραγματικά κλείσει.`,
      ],
    },
    {
      heading: "Τα έγγραφα, εκεί όπου θα τα βρείτε",
      content: [
        `Ένα αρχείο είναι καλό ευρετήριο και κακή ντουλάπα, και η διάκριση αξίζει να κρατηθεί: περισσότερος χρόνος οργάνωσης απ' όσο φαντάζεστε φεύγει στο να ψάχνετε ένα έγγραφο που σίγουρα υπάρχει.`,
        `Πράγματα που πρέπει να βρίσκονται γρήγορα:`,
        {
          type: "list",
          items: [
            `υπογεγραμμένα συμφωνητικά και κάθε αναθεωρημένη εκδοχή`,
            `προσφορές, τιμολόγια και αποδείξεις`,
            `γραπτή επιβεβαίωση για ό,τι συμφωνήθηκε με email ή μήνυμα`,
            `συνδέσμους: μια διάταξη αίθουσας, έναν κοινό φάκελο, μια πλατφόρμα συνεργάτη`,
            `σημειώσεις και φωτογραφίες από ραντεβού και επισκέψεις`,
          ],
        },
        `Αντί να αντιγράφετε το περιεχόμενο ενός συμφωνητικού σε ένα κελί, κρατήστε τα αρχεία κάπου σταθερά που ελέγχετε εσείς και δείξτε προς αυτά: τι είναι το έγγραφο, σε ποιον ανήκει, πού βρίσκεται. Το αρχείο σας λέει ότι η συμφωνία υπάρχει και είναι υπογεγραμμένη· το έγγραφο είναι η συμφωνία.`,
        `Και διαλέξτε ένα σημείο, γιατί έγγραφα μοιρασμένα σε δύο email, ένα κινητό και έναν κοινό φάκελο είναι, στην πράξη, έγγραφα που δεν έχετε.`,
      ],
    },
    {
      heading: "Ένα χρονοδιάγραμμα",
      content: [
        `Οι εργασίες σάς λένε τι πρέπει να γίνει. Το χρονοδιάγραμμα σάς λέει πότε πέφτει η κάθε ημερομηνία του πλάνου, που είναι άλλη ερώτηση και συνήθως απαντιέται αργά.`,
        `Είναι μια σύντομη λίστα ημερομηνιών και όχι δεύτερο αντίγραφο των εργασιών:`,
        {
          type: "list",
          items: [
            `τα κλεισίματα που πρέπει να γίνουν πριν προχωρήσει οτιδήποτε άλλο`,
            `η ημερομηνία που φεύγουν τα προσκλητήρια, και η προθεσμία απάντησης`,
            `οι ημερομηνίες που ο κάθε συνεργάτης θέλει τελικούς αριθμούς`,
            `οι ημερομηνίες των τελικών πληρωμών`,
            `η τελευταία εβδομάδα: επιβεβαιώσεις, τελικοί αριθμοί, παραδόσεις, ό,τι πρέπει να μαζευτεί`,
            `η ίδια η μέρα, μόλις υπάρξει το πρόγραμμά της`,
          ],
        },
        `Τα περισσότερα από αυτά δεν τα διαλέγετε εσείς. Η προθεσμία ενός συνεργάτη για τελικούς αριθμούς και η ημερομηνία μιας εξόφλησης σας δίνονται, και η χρήσιμη δουλειά είναι να μπουν όλα σε μία εικόνα αρκετά νωρίς ώστε να μπορείτε να σχεδιάσετε ανάποδα. Δύο μεγάλες πληρωμές που πέφτουν το ίδιο δεκαπενθήμερο είναι κάτι που θέλετε να ανακαλύψετε τον Μάρτιο.`,
        {
          type: "p",
          text: [
            `Το πρόγραμμα της ίδιας της ημέρας ανήκει κι αυτό εδώ, αν και φτάνει αργά και είναι έγγραφο από μόνο του. Για τη σειρά με την οποία γίνεται η υπόλοιπη δουλειά, το `,
            {
              text: "Checklist γάμου: τι να οργανώσετε και πότε",
              href: "/guides/wedding-planning-checklist",
            },
            ` περνά όλο το πλάνο φάση προς φάση.`,
          ],
        },
      ],
    },
    {
      heading: "Πώς μοιάζει ένα αρχείο που δουλεύει",
      content: [
        `Μαζεμένα, ένα αρχείο που κρατά ένα πλάνο χωρίς να γίνει το ίδιο πλάνο είναι περίπου οκτώ περιοχές, με λίγα πεδία η καθεμία:`,
        {
          type: "list",
          items: [
            `Εργασίες: η ενέργεια, ο υπεύθυνος, η προθεσμία, η κατάσταση, μια σημείωση`,
            `Καλεσμένοι: το άτομο, το σπίτι του, στοιχεία επικοινωνίας, η απάντησή του`,
            `Συνεργάτες: η επιχείρηση και η επαφή, τι παρέχουν, πού βρίσκεται η συζήτηση, αν υπάρχει υπογραφή, το συμφωνημένο σύνολο`,
            `Προϋπολογισμός: η κατηγορία ή το αντικείμενο, εκτιμώμενο, προσφορά, δεσμευμένο`,
            `Πληρωμές: ο συνεργάτης, μια περιγραφή, το ποσό, η ημερομηνία, πληρωμένο ή όχι`,
            `Χρονοδιάγραμμα: η ημερομηνία, τι πέφτει, ποιος το βλέπει`,
            `Αποφάσεις: το ερώτημα, ανοιχτό ή κλειστό, η απάντηση, το γιατί`,
            `Έγγραφα: τι είναι, σε ποιον ανήκει, πού βρίσκεται`,
          ],
        },
        `Είναι αφετηρία και όχι προδιαγραφή, και δεν είναι οκτώ φύλλα να φτιάξετε το πρώτο απόγευμα: ένα άδειο φύλλο δεν είναι ουδέτερο, διαβάζεται ως δουλειά που δεν κάνατε.`,
        `Μια περιοχή δικαιώνει τη θέση της όταν λύνει ένα πρόβλημα που όντως έχετε. Τα τραπέζια είναι το καθαρότερο παράδειγμα: για το μεγαλύτερο μέρος της οργάνωσης δεν είναι καν πραγματική περιοχή, γιατί δεν μπορείτε να βάλετε σε τραπέζι ανθρώπους που δεν έχουν απαντήσει.`,
        `Ξεκινήστε από εργασίες, καλεσμένους και χρήματα, και αφήστε τα υπόλοιπα να έρθουν όταν κάτι τα επιβάλλει.`,
      ],
    },
    {
      heading: "Κρατήστε το απλό και ενημερωμένο",
      content: [
        { type: "subheading", text: "Λιγότερες στήλες από όσες δίνει ένα πρότυπο" },
        `Ένα θεωρητικά πλήρες αρχείο που δεν άνοιξε κανείς από τον Απρίλιο είναι χειρότερο από ένα λιτό που είναι ενημερωμένο. Ό,τι κρατά ένα αρχείο κοστίζει κάτι για να μείνει αληθινό, και αυτό το κόστος πληρώνεται κάθε εβδομάδα.`,
        `Τα περισσότερα από όσα το κάνουν ασυντήρητο αποφεύγονται:`,
        {
          type: "list",
          items: [
            `στήλες που προστέθηκαν για λόγους πληρότητας και δεν συμπληρώθηκαν ποτέ`,
            `το ίδιο δεδομένο γραμμένο σε δύο σημεία`,
            `φύλλα φτιαγμένα εκ των προτέρων για δουλειά που δεν έχει αρχίσει`,
            `καταστάσεις τόσο λεπτομερείς που πρέπει να το σκεφτείτε πριν διαλέξετε`,
            `τύποι και μορφοποιήσεις τόσο περίπλοκες που φοβάστε να αλλάξετε μια γραμμή`,
            `μια δομή που καταλαβαίνει μόνο ο ένας από τους δύο`,
          ],
        },
        `Δείτε κάθε άδεια στήλη ως ερώτηση: αν είναι κενή έναν μήνα, ή δεν είναι πληροφορία που χρειάζεστε ή ζει κάπου αλλού. Και αν το να καταγράψετε μια απάντηση σημαίνει τρία κελιά και έναν έλεγχο σε ένα σύνολο, δεν θα γίνει μια Τρίτη βράδυ.`,
        { type: "subheading", text: "Πόσο συχνά το ενημερώνετε" },
        `Δεν υπάρχει σωστό διάστημα, και ένα πρόγραμμα είναι λιγότερο χρήσιμο από μια σύντομη λίστα αφορμών:`,
        {
          type: "list",
          items: [
            `μετά από μια απόφαση, όσο θυμάστε ακόμη το σκεπτικό`,
            `μετά από ένα κλείσιμο, ή όταν υπογραφεί ή αλλάξει μια συμφωνία`,
            `μετά από κάθε πληρωμή που φεύγει`,
            `όταν έρχεται μια απάντηση ή αλλάζουν τα στοιχεία ενός καλεσμένου`,
            `μετά από κάθε συζήτηση στην οποία συμφωνήθηκε κάτι`,
          ],
        },
        `Ανάμεσά τους, ένα σύντομο επαναλαμβανόμενο πέρασμα κρατά τους αριθμούς τίμιους. Πολλά ζευγάρια βρίσκουν άνετο ένα εβδομαδιαίο μόλις αρχίσουν τα κλεισίματα, και μηνιαίο πριν από αυτό. Ο σωστός ρυθμός είναι αυτός που κρατάτε.`,
      ],
    },
    {
      heading: "Excel ή εργαλείο οργάνωσης;",
      content: [
        { type: "subheading", text: "Πότε το Excel αρχίζει να εμποδίζει" },
        `Το Excel είναι εξαιρετικό εργαλείο οργάνωσης, και υπάρχει ένα σχήμα προβλήματος που δεν μπορεί να λύσει. Τα συμπτώματα αναγνωρίζονται:`,
        {
          type: "list",
          items: [
            `ο ίδιος καλεσμένος, συνεργάτης ή αριθμός εμφανίζεται σε παραπάνω από ένα σημείο`,
            `υπάρχουν δύο εκδοχές του αρχείου και καμία δεν είναι σαφώς η τρέχουσα`,
            `το να αλλάξετε έναν αριθμό σημαίνει να αλλάξετε άλλους τρεις στο χέρι`,
            `μια πληρωμή ξεχάστηκε γιατί καθόταν σε ένα φύλλο που δεν ανοίγει κανείς`,
            `οι καταστάσεις έχουν ξεφύγει, και οι μισές γραμμές λένε κάτι που έπαψε να ισχύει`,
          ],
        },
        `Όλα αυτά είναι το ίδιο πράγμα από κάτω: ένα αρχείο αποθηκεύει τιμές και δεν ξέρει τι σημαίνει η μία για την άλλη. Είκοσι αρνήσεις δεν μετακινούν τον αριθμό του catering, τα τραπέζια ή το κόστος ανά άτομο.`,
        `Το να κάνετε αυτή την αριθμητική στο χέρι είναι απολύτως λογικό για έναν απλό γάμο, και παύει να είναι λογικό περίπου στο σημείο όπου αντιγράφετε μία αλλαγή σε τρία σημεία και δεν είστε πια σίγουροι ότι τα προλάβατε όλα.`,
        { type: "subheading", text: "Το Excel δουλεύει καλά όταν" },
        {
          type: "list",
          items: [
            `θέλετε να σχεδιάσετε μόνοι σας τη δομή και να την αλλάζετε ελεύθερα`,
            `σας βολεύει να το συντηρείτε, και θα το κάνετε`,
            `ο γάμος είναι σχετικά απλός`,
            `η δουλειά είναι κυρίως αριθμητική, που είναι ακριβώς αυτό στο οποίο είναι καλύτερο`,
          ],
        },
        { type: "subheading", text: "Ένα συνδεδεμένο σύστημα βοηθά όταν" },
        {
          type: "list",
          items: [
            `πολλές περιοχές πρέπει να μένουν συγχρονισμένες μεταξύ τους`,
            `οι αλλαγές στους καλεσμένους περνούν σε φαγητό, τραπέζια και κόστος`,
            `περισσότεροι από ένας άνθρωποι ενημερώνουν πληροφορία`,
            `αντιγράφετε συνέχεια το ίδιο δεδομένο ανάμεσα σε λίστες`,
            `εργασίες, συνεργάτες, κόστη και απαντήσεις πρέπει να είναι σωστά ταυτόχρονα`,
          ],
        },
        `Πολλά ζευγάρια καταλήγουν και με τα δύο, που είναι θεμιτή απάντηση και όχι αδυναμία επιλογής: ένα αρχείο για τους υπολογισμούς, ένα εργαλείο για όσα πρέπει να μένουν συνδεδεμένα. Αυτό που δεν δουλεύει είναι η ίδια πληροφορία να ζει και στα δύο.`,
      ],
    },
    {
      heading: "Όλο το πλάνο σε ένα σημείο",
      content: [
        {
          type: "note",
          text: `Ο χώρος οργάνωσης και ο Ἔρως στο Agapaé περιλαμβάνονται στο πακέτο Heirloom.`,
        },
        `Ο χώρος οργάνωσης του Agapaé είναι το σύστημα που περιγράφεται παραπάνω, ήδη στημένο.`,
        `Η οργάνωση είναι χωρισμένη στα μέρη από τα οποία αποτελείται ένας γάμος, που όλα αλλάζουν, και το καθένα κρατά τους δικούς του συνεργάτες, εργασίες, κόστη, έγγραφα και ανοιχτές αποφάσεις. Η καρτέλα ενός συνεργάτη κρατά το άτομο με το οποίο μιλάτε, τα στοιχεία του και τις σημειώσεις σας, με μια κατάσταση που ακολουθεί την πραγματική συζήτηση. Μια εργασία κρατά προθεσμία, προτεραιότητα, μικρότερα βήματα και τις δικές σας ετικέτες, και επισημαίνεται όταν περάσει η ημερομηνία της. Ένα κόστος κρατά περιγραφή, ποσό, ημερομηνία και τον συνεργάτη στον οποίο ανήκει, εκκρεμεί ή πληρώθηκε, και αθροίζεται σε τέσσερα σύνολα.`,
        `Και οι δύο περιοχές που λείπουν από τα περισσότερα αρχεία υπάρχουν κι αυτές. Τα ανοιχτά ερωτήματα καταγράφονται ως αποφάσεις, με τις επιλογές που ζυγίζετε, και κρατούν την απάντησή τους μόλις κλείσουν. Συμβόλαια, προσφορές και αποδείξεις μπαίνουν ως αρχεία στον συνεργάτη ή στην εργασία που τους ανήκει. Ό,τι έχει ημερομηνία μοιράζεται ένα ημερολόγιο, και τα ορόσημα του πλάνου κάθονται σε ένα χρονοδιάγραμμα.`,
        `Ο Ἔρως ζει μέσα σε αυτόν τον χώρο. Διαβάζει ό,τι υπάρχει και απαντά από αυτό, και όταν του το ζητήσετε κάνει ο ίδιος την αλλαγή: προσθέτει την εργασία, καταγράφει την πληρωμή, ενημερώνει τον συνεργάτη. Προτείνει και αποφασίζετε εσείς.`,
        `Και αξίζει να ειπωθεί καθαρά τι δεν κάνει. Το Agapaé δεν εισάγει υπάρχον αρχείο και δεν συγχρονίζεται με το Excel ή με τα Google Sheets. Δεν είναι κατάλογος επαγγελματιών, δεν επικοινωνεί, δεν κλείνει και δεν πληρώνει κανέναν μέσα από αυτό, και δεν στέλνει υπενθυμίσεις για τις πληρωμές των συνεργατών σας. Δεν είναι λογιστικό πρόγραμμα, και δεν αντικαθιστά τα υπογεγραμμένα σας συμφωνητικά.`,
        {
          type: "p",
          text: [
            `Η οργάνωση περιγράφεται αναλυτικά στη σελίδα `,
            { text: "Οργάνωση γάμου", href: "/wedding-planning" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Τι πρέπει να περιλαμβάνει ένα αρχείο οργάνωσης γάμου;`,
      a: `Τουλάχιστον: εργασίες με υπεύθυνο και προθεσμία, τη λίστα καλεσμένων, τους συνεργάτες, τον προϋπολογισμό, τις πληρωμές που πέφτουν και ένα χρονοδιάγραμμα με τις ημερομηνίες από τις οποίες κρέμεται το πλάνο. Δύο ακόμη αξίζουν και συνήθως λείπουν: οι αποφάσεις που πήρατε, ώστε να μην ξανανοίγουν, και το πού βρίσκεται κάθε συμφωνητικό και προσφορά.`,
    },
    {
      q: `Πόσα φύλλα να έχει;`,
      a: `Λιγότερα από όσα δίνουν τα περισσότερα πρότυπα. Εργασίες, καλεσμένοι και χρήματα είναι τα τρία που θα χρησιμοποιείτε από την πρώτη εβδομάδα, και οι συνεργάτες ακολουθούν μόλις αρχίσετε να μιλάτε σε κόσμο. Τα τραπέζια, το ταξίδι και τα υπόλοιπα μπαίνουν όταν γίνουν πραγματική δουλειά, γιατί ένα άδειο φύλλο διαβάζεται ως κάτι που δεν κάνατε.`,
    },
    {
      q: `Να βάλω τις πληρωμές στο ίδιο φύλλο με τον προϋπολογισμό;`,
      a: `Όχι. Ο προϋπολογισμός είναι πλάνο για το τι πρέπει να κοστίσει κάτι· οι πληρωμές είναι γεγονότα με ημερομηνία και κατάσταση. Δώστε στην κάθε πληρωμή δική της γραμμή και κρατήστε τα δεσμευμένα χωριστά από τα πληρωμένα. Αν αφαιρείτε μόνο τα πληρωμένα από τον προϋπολογισμό, διαβάζετε ως διαθέσιμα χρήματα που ήδη οφείλετε.`,
    },
    {
      q: `Πόσο συχνά να το ενημερώνω;`,
      a: `Όχι με πρόγραμμα, αλλά με αφορμές: μετά από μια απόφαση, μετά από ένα κλείσιμο, μετά από μια πληρωμή, όταν έρχεται μια απάντηση, και μετά από κάθε συζήτηση στην οποία συμφωνήθηκε κάτι. Πολλά ζευγάρια προσθέτουν ένα σύντομο εβδομαδιαίο πέρασμα μόλις αρχίσουν τα κλεισίματα.`,
    },
    {
      q: `Χρειάζομαι οπωσδήποτε αρχείο Excel;`,
      a: `Χρειάζεστε ένα σημείο όπου ζει το πλάνο· το Excel είναι ένας καλός τρόπος να το έχετε. Ταιριάζει σε όσους θέλουν να φτιάξουν μόνοι τους τη δομή και θα τη συντηρήσουν, και τα καταφέρνει με την αριθμητική καλύτερα από τα περισσότερα. Δυσκολεύει όταν πολλές περιοχές πρέπει να μένουν συγχρονισμένες ή όταν αντιγράφετε την ίδια αλλαγή σε τρία σημεία.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["wedding-planning-checklist", "how-to-track-wedding-vendors-payments"],

    product: [
      {
        href: "/wedding-planning",
        label: "Οργάνωση γάμου",
        blurb:
          "Δείτε πώς ο χώρος οργάνωσης κρατά μαζί εργασίες, συνεργάτες, κόστη, έγγραφα και ανοιχτές αποφάσεις σε ένα ιδιωτικό σημείο, με ημερολόγιο και χρονοδιάγραμμα από πάνω. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default weddingPlanningSpreadsheetWhatToTrackEl;
